import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import minMax from "dayjs/plugin/minMax";
import { IDateValue } from "./DateFormatter";
dayjs.extend(utc);
dayjs.extend(minMax);

export class DatesCalculator {
    static addMonths(date: IDateValue, months: number) {
        return dayjs(date).add(months, "months");
    }

    /**
     *
     * @returns object {start: string, end: string} string is in format '2019-01-25T02:00:00' or empty string when no value provided
     */
    static getStartAndEndOf(date: IDateValue, unit: dayjs.OpUnitType) {
        const castedDate = dayjs(date, { utc: true });
        const format = "YYYY-MM-DDTHH:mm:ss";
        if (castedDate) {
            const start = castedDate.startOf(unit).format(format);
            const end = castedDate.endOf(unit).format(format);
            return { start, end };
        }
        return { start: "", end: "" };
    }
    /**
     *
     * @param date
     * @param addMonthsBy calculates new date by number of months - can be negative number
     * @returns
     */
    static calculateNewMonth(date: IDateValue, addMonthsBy: number) {
        if (date) {
            const currentMonth = dayjs(date).get("month");
            const lastMonth = dayjs(date).set("month", currentMonth + addMonthsBy);
            return lastMonth;
        }
        return null;
    }

    static getCurrentFiscalYear() {
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();
        let fyStart = dayjs("2023-04-01");
        let fyEnd = dayjs("2024-03-31");
        if (fyStart.month() > currentMonth) {
            fyStart = fyStart.set("year", currentYear - 1);
            fyEnd = fyEnd.set("year", currentYear);
        } else {
            fyStart = fyStart.set("year", currentYear);
            fyEnd = fyEnd.set("year", currentYear + 1);
        }
        return { fyStart, fyEnd };
    }

    static getCurrentFiscalQuarter() {
        const today = dayjs();
        const currentMonth = today.month();
        const currentYear = today.year();

        const fiscalYearStartMonth = 2; // March
        let fiscalQuarterStartMonth = fiscalYearStartMonth;
        let fiscalYear = currentYear;

        if (currentMonth < fiscalYearStartMonth) {
            fiscalQuarterStartMonth = fiscalYearStartMonth - 2;
            fiscalYear -= 1;
        }

        const quarterIndex = Math.ceil((currentMonth - fiscalQuarterStartMonth + 1) / 3);
        const quarterStartMonth = fiscalQuarterStartMonth + 3 * (quarterIndex - 1);
        const quarterStartDate = today.set("year", fiscalYear).set("month", quarterStartMonth).startOf("month");
        const quarterEndDate = today
            .set("year", fiscalYear)
            .set("month", quarterStartMonth + 3)
            .endOf("month");

        return { quarterStartDate, quarterEndDate };
    }

    static getCurrentCalendarYear() {
        const today = dayjs();
        const yearStart = today.startOf("year");
        const yearEnd = today.endOf("year");
        return { yearStart, yearEnd };
    }

    static getCurrentCalendarYearQuarter() {
        const currentDate = dayjs();
        const currentYear = currentDate.year();
        const currentMonth = currentDate.month();

        let quarterStartMonth: number;
        let quarterEndMonth: number;

        if (currentMonth >= 0 && currentMonth <= 2) {
            quarterStartMonth = 0; // January
            quarterEndMonth = 2; // March
        } else if (currentMonth >= 3 && currentMonth <= 5) {
            quarterStartMonth = 3; // April
            quarterEndMonth = 5; // June
        } else if (currentMonth >= 6 && currentMonth <= 8) {
            quarterStartMonth = 6; // July
            quarterEndMonth = 8; // September
        } else {
            quarterStartMonth = 9; // October
            quarterEndMonth = 11; // December
        }

        const startDate = dayjs().set("year", currentYear).set("month", quarterStartMonth).startOf("month");
        const endDate = dayjs().set("year", currentYear).set("month", quarterEndMonth).endOf("month");

        return { startDate, endDate };
    }

    static getDifferenceBetween(firstDate: IDateValue, secondDate: IDateValue, unit: dayjs.ManipulateType): number {
        return dayjs(firstDate).diff(secondDate, unit);
    }

    static addAmountByUnit(date: IDateValue, amount: number, unit: dayjs.ManipulateType): Dayjs {
        return dayjs(date).add(amount, unit);
    }

    static subtractAmountByUnit(date: IDateValue, amount: number, unit: dayjs.ManipulateType): Dayjs {
        return dayjs(date).subtract(amount, unit);
    }
    /**
     * Adds a specified number of working days to a given date.
     * @param {Date} date - The starting date.
     * @param {number} days - The number of working days to add. Can be negative to subtract days.
     * @returns {Date | null} - The resulting date after adding the working days, or null for invalid input.
     */
    static addWorkingDays(date: IDateValue, days: number): Date | null {
        if (!date) {
            return null;
        }
        if (days === 0) {
            return dayjs(date).toDate();
        }
        let resultDate = dayjs(date);
        const increment = days > 0 ? 1 : -1; // Determine whether to add or subtract days
        while (days !== 0) {
            // Proceed to the next or previous day
            resultDate = increment === 1 ? resultDate.add(1, "day") : resultDate.subtract(1, "day");
            // Check if the day is a weekday
            if (resultDate.day() !== 0 && resultDate.day() !== 6) {
                days -= increment; // Adjust the days count towards zero
            }
        }
        return resultDate.toDate();
    }
    static dateInRange(date: IDateValue, dateStart: IDateValue, dateEnd: IDateValue) {
        const dateInDayjs = dayjs(date);
        const dateIsGreaterThanStartDate = dateInDayjs.diff(dateStart) >= 0;
        const dateIsLowerThanEndDate = dateInDayjs.diff(dateEnd) <= 0;
        return dateIsGreaterThanStartDate && (dateIsLowerThanEndDate || !dateEnd);
    }
    /**
     * Checks if the first date is greater than the second date.
     *
     * @param firstDate - The first date to compare.
     * @param secondDate - The second date to compare.
     * @param allowEqual - Optional. Default false. Specifies whether the first date can be equal to the second date.
     * @returns True if the first date is greater than the second date, false otherwise.
     */
    static isGreaterThan(firstDate?: Date | Dayjs | string | null, secondDate?: Date | Dayjs | string | null, allowEqual = false) {
        if (!firstDate && !secondDate) {
            return false;
        }
        if (!secondDate) {
            return true;
        }
        if (!firstDate) {
            return false;
        }
        if (allowEqual) {
            const test = dayjs(firstDate).diff(secondDate);
            return test >= 0;
        }
        return dayjs(firstDate).diff(secondDate) > 0;
    }

    static convertSecondsToMinutesSeconds(milliseconds: number): string {
        if (milliseconds < 0) {
            return "00:00";
        }

        const seconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;

        const formattedMinutes = minutes.toString().padStart(2, "0");
        const formattedSeconds = remainingSeconds.toString().padStart(2, "0");

        return `${formattedMinutes}:${formattedSeconds}`;
    }
    static max(...dates: dayjs.Dayjs[]) {
        return dayjs.max(...dates);
    }
    static min(...dates: dayjs.Dayjs[]) {
        return dayjs.min(...dates);
    }
    /**
     *
     * @param birthday
     * @returns string representation of age in years, months or days
     */
    public static getPersonAgeString(birthday: Date | null): string {
        if (!birthday) {
            return "";
        }
        const ageInYears = DatesCalculator.getDifferenceBetween(new Date(), birthday, "years");
        if (ageInYears > 0) {
            const unit = ageInYears > 1 ? "years" : "year";
            return `${ageInYears} ${unit}`;
        }
        const ageInMonths = DatesCalculator.getDifferenceBetween(new Date(), birthday, "months");
        if (ageInMonths > 0) {
            const unit = ageInMonths > 1 ? "months" : "month";
            return `${ageInMonths} ${unit}`;
        }
        const ageInDays = DatesCalculator.getDifferenceBetween(new Date(), birthday, "days");
        if (ageInDays > 0) {
            const unit = ageInDays > 1 ? "days" : "day";
            return `${ageInDays} ${unit}`;
        }
        return "0";
    }
    /**
     *
     * @param birthday
     * @returns age in years
     */
    public static getPersonAge(birthday: Date | null): number {
        if (!birthday) {
            return 0;
        }
        const ageInYears = DatesCalculator.getDifferenceBetween(new Date(), birthday, "years");
        return ageInYears;
    }
}

export type Months = "January" | "February" | "March" | "April" | "May" | "June" | "July" | "August" | "September" | "October" | "November" | "December";
